/* eslint-disable import/default */
import { Autopayment } from 'billing/mediators/billing-autopayment-mediator';
import currentUser from 'system/currentUser';

import Emitter from 'Emitter';

import CreditCard from '../models/CreditCard';
import CreditCardCreator from './CreditCardCreator';

let instance;

class CreditCardManager {
    constructor() {
        if (instance) {
            return instance;
        }
        instance = this;

        this._loadPromise = null;
        this.card = null;
        this._error = null;
    }

    pay(amount, description) {
        return this.card.pay(amount, description).then((res) => {
            this.emit('pay');
            return res;
        });
    }

    getCard() {
        return new Promise((resolve, reject) => {
            if (this.card !== null) {
                resolve(this.card);
            } else {
                this.loadCard()
                    .then(resolve)
                    .catch(reject);
            }
        });
    }

    hasCard() {
        return this.card instanceof CreditCard;
    }

    hasError() {
        return !!this._error;
    }

    getError() {
        return this._error;
    }

    hasInfo() {
        return this.card !== null || this._error !== null;
    }

    async removeCard({ isAutopaymentEnabled }) {
        if (isAutopaymentEnabled) {
            await Autopayment.disable();
        }

        const response = await this.card.remove();

        this.card = null;

        this.emit('change');
        this.emit('remove');

        return response;
    }

    loadCard() {
        this._error = null;

        return (
            this._loadPromise || (this._loadPromise = Promise.resolve())
        ).then(() => {
            // eslint-disable-next-line promise/no-nesting
            return CreditCard.getCard()
                .then((card) => {
                    this._loadPromise = null;
                    this.card = card.eq(0);

                    this.emit('change');
                    this.emit('load');

                    if (this.card) {
                        this._showAutopaymentSettings();
                    }

                    return this.card;
                })

                .catch((e) => {
                    this._loadPromise = null;
                    this.card = null;
                    this._error = e;

                    this.emit('error');

                    throw e;
                });
        });
    }

    getBindingGateURL(user) {
        return CreditCard.getBindingGateURL(user);
    }

    _showAutopaymentSettings() {
        if (this._creditCardCreator) {
            this._creditCardCreator.showAutopaymentSettings();
        }
    }

    _destroyCardBindingDialog() {
        if (this._creditCardCreator) {
            this._creditCardCreator.off();
            this._creditCardCreator.destroy();
            this._creditCardCreator = null;
        }
    }

    showCardBindingDialog() {
        return new Promise((resolve, reject) => {
            const destroyDialogAndResolvePromise = () => {
                const success = this._creditCardCreator.isSuccess();

                this._destroyCardBindingDialog();

                if (success) {
                    resolve();
                } else {
                    reject();
                }
            };

            if (this._creditCardCreator) {
                this._creditCardCreator.destroy();
                this._creditCardCreator = null;
            }

            this._creditCardCreator = new CreditCardCreator({
                user: currentUser.get('email'),
            });

            this._creditCardCreator.on('close', destroyDialogAndResolvePromise);

            this._creditCardCreator.on(
                'autopaymentSettingsChanged',
                destroyDialogAndResolvePromise,
            );

            this._creditCardCreator.on('paySuccess', () => {
                const delayListInSeconds = [5, 10, 20, 40, 80];
                const loadCard = () => {
                    if (!this.card) {
                        this.loadCard();

                        const delayTime = delayListInSeconds.shift();

                        if (isFinite(delayTime)) {
                            setTimeout(loadCard, delayTime * 1000);
                        }
                    }
                };

                loadCard();
            });
        });
    }

    static getInstance() {
        return new CreditCardManager();
    }
}

Emitter.apply(CreditCardManager.prototype);

export { CreditCardManager };
export default CreditCardManager;
